/**
 * Printable per-garment QR label sheet (Rollo Phase 1; style-code render, design-style-code plan Phase 3;
 * two printer formats, label-printer-formats plan § 3).
 * One label per physical garment in a batch, at the chosen printer's physical size — 51×25 mm on the
 * Rollo or 2.25×1.25 in on the DYMO 30334 — carrying a QR (durable identity `v1|store|order|line|owedRank`)
 * with the order # beneath it, front-family STYLE-CODE badges (FR/FW/LC), the back's full code + design
 * name, size, and blank. The style code is live-joined by designId (useStyleCodes) — never denormalized —
 * and falls back to the legacy first-two-letters of the name for a design without a code yet.
 *
 * Reuses the body-level print-portal mechanism (id `#label-print`, `@media print` isolation in globals.css)
 * like the pick-list / PO / pull-sheet portals, and injects the chosen format's `@page` only while it can
 * print (beforeprint/afterprint) so it never bleeds into a letter print.
 *
 * ⚠️ The format picker and Chrome's own printer dropdown are TWO separate choices that must agree, and
 * nothing in the browser can reconcile them — so the toolbar names the printer rather than the paper.
 * Getting them crossed is loud, not silent (a DYMO layout on Rollo stock clips its right edge; the
 * reverse leaves white margin), with ONE exception: Chrome's "Fit to page" scales a mismatched page onto
 * the media and still yields a plausible, scannable label at the wrong size. Hence the Scale = 100%
 * instruction in the hint below.
 *
 * A QR-less pile-tag divider prints before each (body, colour, size) run (`labelStrip`) so the peel run
 * is physically interrupted between piles — see `DividerCard`.
 *
 * Display-only: composes the pure `label-lib` helper with the batch manifest + screen-print work already
 * on the page. No writes, no new endpoint, no money path.
 */
import { useQuery } from '@tanstack/react-query';
import { Check, ClipboardCheck, Printer, X } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { useApiClient } from '@/lib/api/hooks';
import { cn } from '@/lib/utils';
import type { BatchManifest, ScreenPrintWorkItem } from './batch-lib';
import { stripStyleCode } from './design-lib';
import {
  LABEL_FORMATS,
  type LabelFormat,
  labelFormatVars,
  labelPageRule,
  loadLabelFormat,
  saveLabelFormatId,
} from './label-format';
import {
  type GarmentLabel,
  garmentLabels,
  groupLabels,
  type LabelPlacement,
  labelPrintBlockReason,
  labelStrip,
  type PileDivider,
  placementBadge,
} from './label-lib';
import { PullList } from './pull-list';
import { stampPull } from './pull-stamp';
import { useBlankNames } from './use-blank-names';
import { useStyleCodes } from './use-style-codes';

/** Label styling, scoped to the portal. Real-world units throughout, so it prints at exactly the
 * chosen format's physical size — 51×25 mm on the Rollo, 2.25×1.25 in on the DYMO 30334.
 *
 * The SIX values that differ between the two formats are read through `var()` and supplied by
 * `labelFormatVars()` (`label-format.ts`), which is prepended to this string. There are no `var()`
 * fallbacks on purpose: a missing property would silently collapse a length to nothing and print a
 * ruined label, so `label-format.test.ts` asserts the vars block declares exactly the set used here.
 * Everything else below is SHARED — a second copy of this stylesheet would drift from the paper-only
 * findings recorded in these comments.
 *
 * TWO HARD PRINT CONSTRAINTS — keep every rule below pure #000 on bare white:
 *  1. NO `background`/`background-color` as a way to make anything visible. Chrome strips CSS
 *     backgrounds from a printout unless the operator ticks "Background graphics" (off by default),
 *     so a white-on-black pill prints as invisible/gray text. Borders are NOT stripped — box things
 *     with a border instead (that's why the style-code + size badges survive).
 *  2. NO gray tints. The Rollo is a 1-bit direct-thermal head: gray dithers to a muddy stipple that
 *     reads washed-out at 2 mm type. De-emphasize with font-size/weight, never with color.
 * Verified on hardware 2026-08-01 — the black `DTF` pill printed blank and the #666/#333 text faint.
 */
export const LABEL_CSS = `
#label-print .label-list { display: flex; flex-direction: column; }
#label-print .label {
  width: var(--card-w); height: var(--card-h); padding: var(--pad-v) var(--pad-h); box-sizing: border-box;
  background: #fff; color: #000; overflow: hidden;
  display: flex; flex-direction: row; align-items: center; gap: 1.6mm;
}
#label-print .label * { box-sizing: border-box; }
#label-print .qr { flex: none; display: flex; flex-direction: column; align-items: center; gap: var(--qr-gap); }
#label-print .qr svg { display: block; width: var(--qr); height: var(--qr); shape-rendering: crispEdges; }
#label-print .order-under { font-weight: 800; font-size: var(--order); line-height: 1; font-variant-numeric: tabular-nums; white-space: nowrap; }
#label-print .txt { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; justify-content: center; gap: 0.35mm; }
#label-print .hdr { display: flex; align-items: center; justify-content: space-between; gap: 1mm; }
#label-print .badges { display: flex; align-items: center; gap: 1mm; min-width: 0; }
#label-print .badge {
  font-family: ui-monospace, Menlo, Consolas, monospace; font-weight: 800; font-size: 5.6mm;
  line-height: 1; letter-spacing: 0.2mm; border: 0.35mm solid #000; border-radius: 1mm; padding: 0.4mm 1.1mm;
}
#label-print .badge.nofront { font-weight: 600; }
#label-print .size { flex: none; font-weight: 800; font-size: 3mm; border: 0.3mm solid #000; border-radius: 0.8mm; padding: 0.1mm 1mm; }
#label-print .brow { display: flex; align-items: baseline; gap: 1.4mm; white-space: nowrap; overflow: hidden; line-height: 1.1; margin-top: 0.5mm; }
#label-print .brow .bk { flex: none; font-size: 1.9mm; font-weight: 800; letter-spacing: 0.12mm; text-transform: uppercase; }
#label-print .brow .bcode { font-family: ui-monospace, Menlo, Consolas, monospace; font-weight: 800; font-size: 3mm; letter-spacing: 0.15mm; }
#label-print .brow .name-fallback { font-size: 2.4mm; font-weight: 600; overflow: hidden; text-overflow: ellipsis; }
#label-print .brow .m { margin-left: auto; flex: none; font-size: 1.8mm; font-weight: 800; letter-spacing: 0.1mm; border: 0.25mm solid #000; border-radius: 0.6mm; padding: 0.15mm 0.7mm; }
#label-print .dname { font-size: 2.6mm; font-weight: 800; line-height: 1.08; margin: 0.15mm 0 0; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
#label-print .blank { font-size: 2.3mm; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 0.4mm; }
#label-print .foot { font-size: 2.05mm; font-variant-numeric: tabular-nums; }
@media screen {
  #label-print .label { box-shadow: 0 0 0 1px #d4d4d8, 0 6px 18px rgba(0,0,0,.10); margin: 0 auto 12px; }
}
@media print {
  #label-print .label { box-shadow: none; margin: 0; page-break-after: always; break-after: page; }
  #label-print .label:last-of-type { page-break-after: auto; break-after: auto; }
}
/* NB: no backticks in the comments below. Biome re-lexes this template literal when it formats the file
   and rewrites a backtick-quoted span into broken CSS — it silently mangled three of these on 2026-08-01,
   and the damage only shows up on paper. */
/* ── Pile divider ───────────────────────────────────────────────────────────────────────────────
   A QR-less 51×25 mm page printed once immediately before each (body, colour, size) run, so the peel
   run is physically interrupted and the pile is tagged.

   THE STYLE IS THE HEADLINE, not the size. The reported mislabel was not a size error: staff sorted
   size L correctly and then assumed every L in the run was an adult tee, when some were hoodies and
   some a youth style. The size is the field they already get right, so the discriminator that failed —
   the blank style — is the biggest glyph on the tag (6.4 mm), the size follows in a box, and the colour
   is the only field allowed to lose its tail (this shop's blanks are almost all Black, so it costs
   nothing; the style number never truncates).

   UNMISTAKABILITY — a garment label's ink is a 17 mm solid QR square jammed against the LEFT edge with
   ragged small type beside it; no garment label has any horizontal black mass or any full-width line of
   6 mm type. The tag is a stack closed by one solid 1.2 mm rule, so at a distance the strip reads as
   square-plus-text cards with a banded block cutting across it — recognised before a character is.
   That rule is a BORDER, never a fill: Chrome drops CSS backgrounds from a printout unless the operator
   ticks "Background graphics", so a filled bar comes off the Rollo blank (real defect, found on paper
   2026-08-01). Same reason there is no gray anywhere — the 1-bit head dithers #333/#666 to a faint
   stipple at this type size, so de-emphasis is by font-size and weight only. Keeping the rule inside
   .label's padding holds it ≥1.3 mm from the paper edge, clear of the head's unprintable margin and of
   feed-registration slop.

   ADDITIVE, and it must stay that way. class="label divider" inherits the 51×25 mm geometry and the
   @media print page-break rules from .label. But the selector #label-print .label.divider is TWO
   classes, so it outranks #label-print .label EVERYWHERE — media queries add no specificity — which is
   why this modifier declares layout only. Re-declaring either break property here silently kills the
   divider's own page break and crushes it onto the first label of its run; re-declaring margin or
   box-shadow loses the screen-only centring. The browser preview looks perfect either way; the defect
   is paper-only, so label-strip-render.test.tsx asserts this block's property list. Keep the root a div
   element as well, or .label:last-of-type (which matches on TAG name, the only structural selector in
   this sheet) starts selecting two nodes and a mid-strip divider loses its page break.

   VERTICAL BUDGET. Content box 22.4 mm (25 − 2×1.3 padding). Style line 6.4 mm + 0.6 gap + size row
   pinned at 7.4 mm by min-height + 0.6 gap + rail (1.2 rule + 0.6 pad + 2.2 text) = 19.0 mm, leaving
   3.4 mm absorbed by justify-content: center. Measured in headless Chrome against this exact
   stylesheet: 25.00 mm box, 1.51 mm of vertical slack, no horizontal overflow, nothing ellipsized on a
   real blank. Re-measure before adding a band; do not just add one. */
#label-print .label.divider { flex-direction: column; align-items: stretch; justify-content: center; gap: 0.6mm; }
/* The discriminator, and the only line that gets a full 47.8 mm to itself.
   MEASURED CAPACITY at 6.4 mm: about 12 characters — "TULTEX 602" (10) and "GILDAN 18500" (12) print
   whole, which is ~80% of what this shop runs. Beyond that it ellipsizes, and because a blank name is
   brand-then-style the tail it loses is the style NUMBER ("RICHARDSON 11…"). Character count is not a
   usable tier threshold here: "Richardson 112" and "Comfort Colors" are both 14 characters and only the
   digit-heavy one fits, so any count-based step would be wrong half the time, and a measurement pass
   would make the printed layout depend on font loading. The accepted residual is that a long
   brand+style truncates its number, with the size + colour row and the peel count still identifying
   the pile. Dropping this to 5.4 mm fits every 14–15 character name (measured) at the cost of the
   common case — a deliberate trade, not an oversight. */
#label-print .sbody { font-size: var(--sbody); font-weight: 800; line-height: 1; letter-spacing: -0.05mm; text-transform: uppercase; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
#label-print .srow { display: flex; align-items: center; gap: 1.2mm; min-height: 7.4mm; overflow: hidden; }
/* flex: none so the size never shrinks — it is what you read off a pile across the shop. max-width +
   ellipsis is the other half: it caps a pathological unmapped size (a raw "LARGE/X-LARGE" variant
   title) so it degrades visibly instead of shoving the colour off the row. */
#label-print .ssize { flex: none; max-width: 26mm; font-size: 7mm; font-weight: 800; line-height: 1; border: 0.45mm solid #000; border-radius: 1mm; padding: 0.2mm 1.2mm; font-variant-numeric: tabular-nums; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
/* Touching the size on purpose: the hazard is a youth garment whose variant size is a bare L, which
   sorts in among the adult L tees, so YOUTH has to land in the same eye fixation as the size glyph. */
#label-print .syouth { flex: none; font-size: 2.9mm; font-weight: 800; line-height: 1; letter-spacing: 0.1mm; border: 0.35mm solid #000; border-radius: 0.6mm; padding: 0.3mm 0.7mm; white-space: nowrap; }
#label-print .scolor { flex: 1 1 auto; min-width: 0; font-size: 3.4mm; font-weight: 700; line-height: 1; text-transform: uppercase; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
/* The peel checksum and your place in the batch, under the closing rule. Small on purpose: both are
   recovery information, read only when something has already gone wrong. */
#label-print .srail { display: flex; align-items: baseline; justify-content: space-between; gap: 1.4mm; border-top: 1.2mm solid #000; padding-top: 0.6mm; font-size: 2.2mm; font-weight: 800; letter-spacing: 0.1mm; line-height: 1; font-variant-numeric: tabular-nums; white-space: nowrap; }
/* ── YOUTH on the GARMENT label ─────────────────────────────────────────────────────────────────
   The reported failure is a youth style whose Shopify variant size is a bare L: it sorts in among the
   adult L tees and reads identically on paper. The marker therefore LEADS the blank line, so the garment
   identity reads as one phrase ("YOUTH · TULTEX 235 · BLACK") — and because .blank already truncates
   from the tail, a long body name can never push the marker off the label.

   It deliberately does NOT sit beside the size. That header row has no horizontal slack: two
   front-family badges at 5.6 mm consume it exactly, so a chip there overflowed .badges on top of the
   size box, and on a 1-bit head the merged strokes destroy the size, the marker and the LC code
   together. Found by measuring the built DOM in Chrome, invisible in the browser preview.

   Boxed, never filled (Chrome drops print backgrounds); line-height 1 with hairline padding so the chip
   cannot grow .blank's line box into the .foot row, which .label { overflow: hidden } would silently cut.
   No gray anywhere — a 1-bit head dithers it to a stipple. */
#label-print .youth { font-weight: 800; font-size: 2.05mm; line-height: 1; letter-spacing: 0.1mm; border: 0.3mm solid #000; border-radius: 0.6mm; padding: 0.2mm 0.6mm; margin-right: 0.8mm; }
/* Belt and braces for the row above, INDEPENDENT of youth: at two badges the header is already exactly
   full, so a wide size (2XL/3XL) alone can overflow it. Clip rather than paint over the size box — a
   clipped second badge is recoverable at a glance, an ink blob is not. */
#label-print .badges { overflow: hidden; }
/* ── Stock BIN label (garment-stock bin-labels plan PR-2) ───────────────────────────────────────
   One tag per stocked shelf identity (bin-label-sheet.tsx). The root is class="label bin" and the
   .bin modifier deliberately has NO rule of its own — the divider block above documents why: a
   two-class selector outranks #label-print .label everywhere, so declaring layout there silently
   steals the page break. The card INHERITS the garment label's QR-left/text-right silhouette (a bin
   tag lives on a bag, never on a garment, so silhouette collision does not matter here the way it
   does for the divider), and the text column reuses .ssize (size IS the headline — the
   most-grabbed-wrong attribute), .blank and .brow/.bk/.bcode/.name-fallback verbatim.
   Same two hard print constraints as everything above: every box is a BORDER (Chrome strips print
   backgrounds) and nothing is gray (1-bit head). */
#label-print .bin-hdr { display: flex; align-items: center; gap: 1.2mm; overflow: hidden; }
/* Caps the under-QR caption at the QR's own width — a 12-char style code would otherwise widen the
   flex-none QR column and shove the text block off the card (layout-only; typography inherits from
   .order-under). */
#label-print .bin-under { max-width: var(--qr); overflow: hidden; text-overflow: ellipsis; }
#label-print .bin-kind { flex: none; font-size: 2.9mm; font-weight: 800; line-height: 1; letter-spacing: 0.1mm; border: 0.35mm solid #000; border-radius: 0.6mm; padding: 0.3mm 0.7mm; white-space: nowrap; }
/* Layout option 1 (owner pick 2026-09-07): the dominant design's NAME, bold, up to two lines. The
   mono variant is the style-code/id fallback for a nameless design — it must read as a code. */
#label-print .bin-name { font-size: 3.8mm; font-weight: 800; line-height: 1.08; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
#label-print .bin-name.mono { font-family: ui-monospace, Menlo, Consolas, monospace; letter-spacing: 0.15mm; }
/* Every other placement (the colorway front, a left chest) on one small line — ellipsizes rather
   than wraps, so truncation is visible instead of a silent clip. */
#label-print .bin-plus { font-size: 2.4mm; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
`;

/** The back print: `BACK` + full style code (or the name when there's no code yet), then the complete
 *  design name below on up to two lines — the back is the big custom print, so it keeps its name. */
function BackRow({ back }: { back: LabelPlacement }) {
  const method = back.method === 'screen-print' ? 'SP' : 'DTF';
  // The name is a frozen snapshot that may still carry the code (e.g. "LT-045 Ford Nation"); strip it
  // so it isn't shown twice, and drop the line entirely when the name was ONLY the code.
  const backName = stripStyleCode(back.designName, back.styleCode);
  const showName =
    backName !== '' && backName.toUpperCase() !== (back.styleCode ?? '').toUpperCase();
  return (
    <>
      <div className="brow">
        <span className="bk">BACK</span>
        {back.styleCode ? (
          <span className="bcode">{back.styleCode}</span>
        ) : (
          <span className="name-fallback">{back.designName || '—'}</span>
        )}
        <span className="m">{method}</span>
      </div>
      {back.styleCode && showName && <div className="dname">{backName}</div>}
    </>
  );
}

export function LabelCard({ label }: { label: GarmentLabel }) {
  const blankLine = [label.bodyLabel, label.colorName].filter(Boolean).join(' · ');
  // Front-family placements (front + any extras like left-chest) → boxed style-code badges.
  const frontFamily = [label.front, ...label.extras].filter((p): p is LabelPlacement => p != null);
  return (
    <div className="label">
      <div className="qr">
        {/* level M + a 4-module quiet zone (qrcode.react defaults to L / margin 0). */}
        <QRCodeSVG value={label.code} size={64} level="M" marginSize={4} />
        <div className="order-under">{label.orderName}</div>
      </div>
      <div className="txt">
        <div className="hdr">
          <div className="badges">
            {frontFamily.length > 0 ? (
              frontFamily.map((p) => {
                const b = placementBadge(p);
                // An unbadgeable placement ('—') uses the muted style, like a no-front label.
                return (
                  <span
                    key={`${p.location}-${p.method}`}
                    className={b === '—' ? 'badge nofront' : 'badge'}
                  >
                    {b}
                  </span>
                );
              })
            ) : (
              <span className="badge nofront">—</span>
            )}
          </div>
          {label.sizeName && <span className="size">{label.sizeName}</span>}
        </div>
        {label.back && <BackRow back={label.back} />}
        {/* YOUTH leads the garment-identity line rather than sitting beside the size, because the header
            row has NO horizontal slack: two front-family badges at 5.6 mm already consume it exactly, and
            anything added there overflows the badges ON TOP of the size (measured — a 1-bit head merges
            the strokes into a blob, destroying the size, the marker and the LC code at once). Reading
            "YOUTH · TULTEX 235 · BLACK" as one phrase is also the disambiguation the operator needs: the
            reported failure is a youth style whose size prints as a bare L. */}
        {(blankLine || label.sizeBand === 'youth') && (
          <div className="blank">
            {label.sizeBand === 'youth' && <span className="youth">YOUTH</span>}
            {blankLine}
          </div>
        )}
        <div className="foot">
          copy {label.rank} of {label.copyOf}
        </div>
      </div>
    </div>
  );
}

/** A pile divider — a QR-less 51×25 mm page printed immediately before its garment run.
 *
 *  Two jobs. (1) It physically interrupts the peel run, so labels cannot be carried from one pile onto
 *  the next. (2) Staff pick the whole batch up front, so this gets peeled onto the pile or box and has
 *  to still answer "is this the pile in my hands?" an hour later.
 *
 *  The BLANK STYLE is the headline, not the size — the owner chose this emphasis after seeing the
 *  alternatives, and it is the one that matches the reported failure: staff sorted size L correctly and
 *  then assumed every L was an adult tee. So the style gets a full 47.8 mm line of 6.4 mm type, the size
 *  follows in a box with YOUTH against it, and the colour absorbs any truncation.
 *
 *  Deliberately unscannable and deliberately not garment-shaped: no QR (a scannable divider would
 *  resolve to nothing at the /scan kiosk, or worse be taken for a garment), a full-width stacked layout
 *  instead of the garment label's QR-left/text-right silhouette, and a solid 1.2 mm rule closing it —
 *  no garment label has any horizontal black mass or a full-width line of 6 mm type. Every box is a
 *  BORDER, never a fill, because Chrome strips background graphics from a printout by default; and there
 *  is no gray, because the 1-bit thermal head dithers it to a stipple at this type size.
 *
 *  Batch title is deliberately not printed: the operator just clicked Print on one batch, so it is zero
 *  information for ~2 mm of height, and that height is what makes the style line readable across a room.
 *
 *  Deviation from the plan (`docs/label-mislabel-mitigation-plan-v1.md` §4 Phase 1), recorded here so it
 *  reads as a decision and not an oversight: the plan's mock carried the literal words "this is a pile
 *  tag". `PILE n OF m` says it in space already spent, and the rest of the budget went to the style —
 *  the structural signals (no QR, the closing rule, the 6.4 mm line) are what work at a glance, and
 *  2 mm wording is unreadable at the distance where the confusion actually happens.
 */
export function DividerCard({ divider }: { divider: PileDivider }) {
  const { sizeName, colorName, bodyLabel, count, index, total, sizeBand } = divider;
  return (
    <div className="label divider">
      {/* `—` when no blank resolved: the style line must never be blank, since it is the field an
          operator navigates by. A nameless pile is a prompt to go and look at the order. */}
      <div className="sbody">{bodyLabel || '—'}</div>
      <div className="srow">
        {/* Rendered verbatim: the operator cross-checks this against the garment's own label, so a youth
            `L` stays `L` and the band is spelled out beside it rather than invented as `YL`. A 7 mm `?`
            for an unresolvable size is meant to be alarming — an unidentified pile needs a human. */}
        <span className="ssize">{sizeName || '?'}</span>
        {sizeBand === 'youth' && <span className="syouth">YOUTH</span>}
        <span className="scolor">{colorName}</span>
      </div>
      <div className="srail">
        {/* The peel checksum: what turns "I lost my place" into "I have three labels left and no
            shirts". Position recovers your place once the tag is off the strip and its order is gone. */}
        <span>
          {count} {count === 1 ? 'LABEL' : 'LABELS'}
        </span>
        <span>
          PILE {index} OF {total}
        </span>
      </div>
    </div>
  );
}

export function LabelSheet({
  batchId,
  screenPrintWork,
  batchTitle,
  onClose,
}: {
  batchId: string;
  screenPrintWork: readonly ScreenPrintWorkItem[];
  batchTitle: string;
  onClose: () => void;
}) {
  const client = useApiClient();
  const nameBySku = useBlankNames();
  const styleCodeByDesignId = useStyleCodes();
  // Lazy initial read so the (throwing) storage accessor runs once, not on every render.
  const [format, setFormat] = useState<LabelFormat>(() => loadLabelFormat());
  // Pulled-blanks v2 §2.5 door 2: after the first print, "Tick the pull list" opens the same rows in
  // place (ticks stamped via `labels`); printing posts the advisory `labels` stamp, fail-open.
  const [printedOnce, setPrintedOnce] = useState(false);
  const [showPull, setShowPull] = useState(false);
  const chooseFormat = (f: LabelFormat) => {
    setFormat(f);
    saveLabelFormatId(f.id);
  };

  const manifestQuery = useQuery({
    queryKey: ['batch-manifest', batchId],
    queryFn: async () => {
      const { data, error } = await client.GET('/batches/{batchId}/manifest', {
        params: { path: { batchId } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the batch.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data as BatchManifest;
    },
  });

  // Escape closes the overlay (matches the pick-list / PO / pull-sheet portals) — unless the pull station
  // is open on top: then Escape is ITS close, and this sheet stays for the bench to come back to.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !showPull) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, showPull]);

  // Apply the CHOSEN format's page size ONLY across a print, so it never overrides the letter @page the
  // other portals rely on. beforeprint injects, afterprint removes (the global @page has no `size`, so the
  // later rule wins on `margin` and sets `size`).
  //
  // ⚠️ `format` is in the dep list, and has to be: this effect used to run once with `[]`. Left that way,
  // the listener closes over the format that was active when the portal mounted, so switching the picker
  // updates the on-screen preview (which reads the vars) while the PAGE stays the old size — a 57×32 mm
  // layout emitted onto a 51×25 mm page, or Chrome silently scaling to fit. The preview would look right.
  useEffect(() => {
    const el = document.createElement('style');
    el.textContent = labelPageRule(format);
    const before = () => {
      // While the pull station is open on top, a print is the PULL LIST (letter), never the labels.
      if (showPull) return;
      if (!el.isConnected) document.head.appendChild(el);
    };
    const after = () => el.remove();
    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => {
      window.removeEventListener('beforeprint', before);
      window.removeEventListener('afterprint', after);
      el.remove();
    };
  }, [format, showPull]);

  const manifest = manifestQuery.data;
  // L0. Consulted BEFORE building any label, and it gates the build itself rather than only the Print
  // button: a rendered strip of wrong-ranked labels is a photograph away from being applied by hand.
  const blockReason = manifest ? labelPrintBlockReason(manifest) : null;
  const labels =
    manifest && !blockReason
      ? garmentLabels(manifest, screenPrintWork, nameBySku, styleCodeByDesignId)
      : [];
  const groups = groupLabels(labels);
  // `labels` stays the pure garment list — the count, the Print gate and the empty state all read its
  // length, and folding dividers into it would report "53 labels" for 41 garments. The pages that
  // actually feed through the Rollo are `strip`.
  const strip = labelStrip(labels);
  const dividerCount = strip.length - labels.length;

  return createPortal(
    <div
      id="label-print"
      role="dialog"
      aria-modal="true"
      aria-label="Print labels"
      className={cn(
        'fixed inset-0 z-50 overflow-auto bg-background p-4 sm:p-6 print:static print:overflow-visible print:bg-transparent print:p-0',
        // The pull station's own portal prints while it is open; this one must not print beneath it.
        showPull && 'print:hidden',
      )}
    >
      <style>
        {labelFormatVars(format)}
        {LABEL_CSS}
      </style>
      {/* Toolbar — screen only */}
      <div className="mx-auto mb-4 flex max-w-3xl items-start justify-between gap-2 print:hidden">
        <div>
          <div className="font-semibold">Print labels — {batchTitle}</div>
          <div className="text-sm text-muted-foreground">
            {manifestQuery.isLoading
              ? 'Loading…'
              : blockReason
                ? 'Labels unavailable for this sheet'
                : `${labels.length} label${labels.length === 1 ? '' : 's'} + ${dividerCount} pile divider${dividerCount === 1 ? '' : 's'} = ${strip.length} page${strip.length === 1 ? '' : 's'}`}
            {groups.length > 0 &&
              ` · ${groups
                .map(
                  (g) =>
                    `${g.count}× ${[g.bodyLabel, g.colorName, g.sizeName].filter(Boolean).join(' ')}`,
                )
                .join(' · ')}`}
          </div>
          {!blockReason && (
            <div className="mt-1 text-xs text-muted-foreground">
              Sorted by size — all S, then M, L … A pile-tag divider prints before each group: peel
              it onto that pile as you pick it, then apply only the labels behind it. Stick on the
              back-bottom inside hem.
              <br />
              Laid out for <span className="font-medium text-foreground">{format.name}</span> — pick
              that same printer in the print dialog, with margins <em>None</em> and scale{' '}
              <em>100%</em>, never “Fit”.
            </div>
          )}
        </div>
        <div className="flex flex-none flex-col items-end gap-2">
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setPrintedOnce(true);
                void stampPull(client, batchId, 'labels');
                window.print();
              }}
              disabled={labels.length === 0}
            >
              <Printer className="size-4" />
              Print
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose}>
              <X className="size-4" />
              Close
            </Button>
          </div>
          {printedOnce && !blockReason && (
            <div className="flex flex-wrap items-center justify-end gap-2 text-xs text-muted-foreground">
              <Button size="sm" variant="outline" onClick={() => setShowPull(true)}>
                <ClipboardCheck className="size-4" />
                Tick the pull list
              </Button>
              <span>opens the rows — tick once the stack is sorted, not before the pull.</span>
            </div>
          )}
          {/* Which printer this sheet is laid out for. Sticky per browser profile, so each of the
              bench's Chrome instances remembers its own printer (label-format.ts `loadLabelFormat`).

              ⚠️ Selection is carried by `variant="secondary"` + a tick + `aria-pressed`, never by
              `variant="default"`: this theme's `--primary` and `--destructive` are the SAME red, so a
              primary-filled button in a group reads as "blocked", not "chosen". */}
          {/* A real <fieldset>, not role="group" (biome's useSemanticElements). Its UA border/padding
              are zeroed explicitly rather than trusted to preflight, and the legend is sr-only because
              the group is self-describing on screen. */}
          <fieldset className="m-0 flex flex-wrap justify-end gap-1 border-0 p-0">
            <legend className="sr-only">Label format</legend>
            {LABEL_FORMATS.map((f) => {
              const active = f.id === format.id;
              return (
                <Button
                  key={f.id}
                  size="sm"
                  variant={active ? 'secondary' : 'outline'}
                  aria-pressed={active}
                  onClick={() => chooseFormat(f)}
                >
                  {active && <Check className="size-3.5" />}
                  {f.name}
                </Button>
              );
            })}
          </fieldset>
        </div>
      </div>

      {manifestQuery.isError && (
        <p className="mx-auto max-w-3xl text-sm text-destructive print:hidden">
          {manifestQuery.error instanceof Error ? manifestQuery.error.message : 'Failed to load.'}
        </p>
      )}
      {/* L0: a REFUSAL, distinct from "this sheet happens to have no labels". Staff who clicked the
          button need the reason and the alternative, or they conclude the app is broken and hand-write
          a tag — which is the mislabel this refusal exists to prevent. */}
      {!manifestQuery.isLoading && !manifestQuery.isError && blockReason && (
        <div
          role="alert"
          className="mx-auto max-w-3xl rounded-md border border-destructive/50 p-3 text-sm print:hidden"
        >
          <div className="font-medium">Labels can’t be printed from a reprint sheet</div>
          <p className="mt-1 text-muted-foreground">{blockReason}</p>
        </div>
      )}
      {!manifestQuery.isLoading &&
        !manifestQuery.isError &&
        !blockReason &&
        labels.length === 0 && (
          <p className="mx-auto max-w-3xl text-sm text-muted-foreground print:hidden">
            No labels to print for this batch.
          </p>
        )}

      {showPull && (
        <PullList
          batchId={batchId}
          label={batchTitle}
          screenPrintWork={screenPrintWork}
          via="labels"
          onClose={() => setShowPull(false)}
        />
      )}

      <div className="label-list">
        {strip.map((e) =>
          e.kind === 'divider' ? (
            // A divider has no QR payload to key off. `index` is in the key as well as the group key,
            // because labelStrip emits one divider per contiguous RUN — two runs of one group (see its
            // doc) would otherwise collide on a React key. `pile:` cannot collide with a `v1|…` code.
            <DividerCard key={`pile:${e.divider.index}:${e.divider.key}`} divider={e.divider} />
          ) : (
            <LabelCard key={e.label.code} label={e.label} />
          ),
        )}
      </div>
    </div>,
    document.body,
  );
}
